<script lang="ts">
  import {
    getMonth,
    getYear,
    getDaysInMonth,
    getDay,
    getWeekOfMonth,
    addMonths,
    format,
    isAfter,
    isBefore,
    min,
    max,
    isFuture,
  } from "date-fns";
  import { isSameDay } from "date-fns/fp";
  import { range, any } from "ramda";
  import { noop } from "../util/general";
  import ChevronRight20 from "carbon-icons-svelte/lib/ChevronRight20";
  import ChevronLeft20 from "carbon-icons-svelte/lib/ChevronLeft20";
  import ChevronDown32 from "carbon-icons-svelte/lib/ChevronDown32";

  // Public props
  export let selectedDate: Date | null = null;
  export let markedDates: Date[] = [];
  export let markedDateLabel: string | null = null;
  export let showToday: boolean = false;
  export let showLegend: boolean = false;
  export let disableFuture: boolean = false;
  export let showBorder: boolean = true;

  // for selecting range
  export let selectedDateRangeStart: Date | null = null;
  export let selectedDateRangeEnd: Date | null = null;
  export let isDateRange: boolean = false;

  // Component state
  let today: Date = new Date();
  let viewedYear: number = getYear(today);
  let viewedMonth: number = getMonth(today);
  // date range state
  let anchorDate: Date | null = null;
  let secondaryDate: Date | null = null;
  let hoveredSecondaryDate: Date | null = null;

  // Derived state
  $: viewedDate = new Date(viewedYear, viewedMonth);
  $: daysInMonth = getDaysInMonth(viewedDate);
  $: calendarTitle = format(viewedDate, "MMMM u");
  const dateForViewedMonth = (
    day: number,
    _viewedMonth: number,
    _viewedYear: number
  ): Date => new Date(_viewedYear, _viewedMonth, day);

  const dayOfWeekMapping: Array<string> = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  // Assigns a grid column based on day of the week
  const getColumnClassForDay = (
    day: number,
    _viewedMonth: number,
    _viewedYear: number
  ): string =>
    dayOfWeekMapping[
      getDay(dateForViewedMonth(day, _viewedMonth, _viewedYear))
    ];

  // Assigns a grid row based on week of the month
  const getRowClassForDay = (
    day: number,
    _viewedMonth: number,
    _viewedYear: number
  ): string =>
    `week-${getWeekOfMonth(
      dateForViewedMonth(day, _viewedMonth, _viewedYear)
    )}`;

  // Assigns a class for marked days
  const getIsMarkedClassForDay = (
    day: number,
    _viewedMonth: number,
    _viewedYear: number
  ): string =>
    any(isSameDay(dateForViewedMonth(day, _viewedMonth, _viewedYear)))(
      markedDates
    )
      ? "marked-day"
      : "";

  // Accumulates all classes into a single string for a day

  // helper functions to set classes on date when selecting range
  const isInRange = (
    date: Date,
    _selectedDateRangeStart: Date | null,
    _selectedDateRangeEnd: Date | null,
    _hoveredSecondaryDate: Date | null,
    _anchorDate: Date | null
  ): boolean =>
    !!(
      isDateRange &&
      ((_selectedDateRangeStart &&
        _selectedDateRangeEnd &&
        isAfter(date, _selectedDateRangeStart) &&
        isBefore(date, _selectedDateRangeEnd)) ||
        (_anchorDate &&
          _hoveredSecondaryDate &&
          isAfter(date, min([_anchorDate, _hoveredSecondaryDate])) &&
          isBefore(date, max([_anchorDate, _hoveredSecondaryDate]))))
    );

  const isSelectedStart = (
    date: Date,
    _selectedDateRangeStart: Date | null,
    _selectedDateRangeEnd: Date | null,
    _hoveredSecondaryDate: Date | null,
    _anchorDate: Date | null,
    _isDateRange: boolean
  ): boolean => {
    if (!isDateRange) {
      return false;
    }
    const isAnchor = _anchorDate && isSameDay(date)(_anchorDate);
    const isHover =
      _hoveredSecondaryDate && isSameDay(date)(_hoveredSecondaryDate);
    const isSelectedStart =
      _selectedDateRangeStart && isSameDay(date)(_selectedDateRangeStart);
    const anchorBeforeHover =
      _anchorDate &&
      _hoveredSecondaryDate &&
      isBefore(_anchorDate, _hoveredSecondaryDate);
    const hoverBeforeAnchor =
      _anchorDate &&
      _hoveredSecondaryDate &&
      isBefore(_hoveredSecondaryDate, _anchorDate);
    return !!(
      isSelectedStart ||
      (isAnchor && anchorBeforeHover) ||
      (isHover && hoverBeforeAnchor)
    );
  };

  const isSelectedEnd = (
    date: Date,
    _selectedDateRangeStart: Date | null,
    _selectedDateRangeEnd: Date | null,
    _hoveredSecondaryDate: Date | null,
    _anchorDate: Date | null,
    _isDateRange: boolean
  ): boolean => {
    if (!_isDateRange) {
      return false;
    }
    const isAnchor = _anchorDate && isSameDay(date)(_anchorDate);
    const isHover =
      _hoveredSecondaryDate && isSameDay(date)(_hoveredSecondaryDate);
    const isSelectedEnd =
      _selectedDateRangeEnd && isSameDay(date)(_selectedDateRangeEnd);
    const anchorAfterHover =
      _anchorDate &&
      _hoveredSecondaryDate &&
      isAfter(_anchorDate, _hoveredSecondaryDate);
    const hoverAfterAnchor =
      _anchorDate &&
      hoveredSecondaryDate &&
      isAfter(hoveredSecondaryDate, _anchorDate);
    return !!(
      isSelectedEnd ||
      (isAnchor && anchorAfterHover) ||
      (isHover && hoverAfterAnchor)
    );
  };
  const isHoveredSecondaryDate = (
    date: Date,
    _hoveredSecondaryDate: Date | null
  ): boolean =>
    !!(_hoveredSecondaryDate && isSameDay(date)(_hoveredSecondaryDate));

  const isAnchorDate = (date: Date, _anchorDate: Date | null): boolean =>
    !!(_anchorDate && isSameDay(date)(_anchorDate));

  const isSelectedStartOrEnd = (
    date: Date,
    _selectedDateRangeStart: Date | null,
    _selectedDateRangeEnd: Date | null,
    _hoveredSecondaryDate: Date | null,
    _anchorDate: Date | null,
    _isDateRange: boolean
  ) =>
    isAnchorDate(date, _anchorDate) ||
    isHoveredSecondaryDate(date, _hoveredSecondaryDate) ||
    isSelectedEnd(
      date,
      _selectedDateRangeStart,
      _selectedDateRangeEnd,
      _hoveredSecondaryDate,
      _anchorDate,
      _isDateRange
    ) ||
    isSelectedStart(
      date,
      _selectedDateRangeStart,
      _selectedDateRangeEnd,
      _hoveredSecondaryDate,
      _anchorDate,
      _isDateRange
    );

  // Callback for increasing viewed month by given amount
  const modifyMonth = (n: number) => () => {
    const newDate = addMonths(viewedDate, n);
    viewedYear = getYear(newDate);
    viewedMonth = getMonth(newDate);
  };

  const selectDate = (clickedDate: Date) => () => {
    if (!isDateRange) {
      selectedDate = clickedDate;
      return;
    }
    if (anchorDate === null && secondaryDate === null) {
      anchorDate = clickedDate;
      return;
    }
    if (anchorDate && secondaryDate === null) {
      secondaryDate = clickedDate;
      hoveredSecondaryDate = null;
      selectedDateRangeStart = min([anchorDate, secondaryDate]);
      selectedDateRangeEnd = max([anchorDate, secondaryDate]);
      anchorDate = null;
      return;
    }
    if (secondaryDate) {
      anchorDate = clickedDate;
      secondaryDate = null;
      hoveredSecondaryDate = null;
      selectedDateRangeStart = null;
      selectedDateRangeEnd = null;
    }
  };

  const setHoverDate = (hoveredDate: Date) => () => {
    if (
      !isDateRange ||
      (anchorDate && secondaryDate) ||
      (anchorDate === null && secondaryDate === null)
    ) {
      return;
    }
    if (anchorDate && secondaryDate === null) {
      hoveredSecondaryDate = hoveredDate;
    }
  };
  $: getIdForDayDiv = (day: number): string =>
    selectedDate &&
    isSameDay(new Date(viewedYear, viewedMonth, day))(selectedDate)
      ? "selected-day"
      : "";
  $: selectedStartAndEndAreSame =
    selectedDateRangeStart &&
    selectedDateRangeEnd &&
    isSameDay(selectedDateRangeStart)(selectedDateRangeEnd);
  const dayOfWeekAbbrvs = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const getBaseClassesForDay = (
    day: number,
    _viewedMonth: number,
    _viewedYear: number
  ): string =>
    [getColumnClassForDay, getRowClassForDay, getIsMarkedClassForDay]
      .map((f) => f(day, _viewedMonth, _viewedYear))
      .join(" ");

  const includeClass = (flag: boolean, className: string) =>
    flag ? className : "";
  const calculateClassesForDay = (
    day: number,
    _selectedDateRangeStart: Date | null,
    _selectedDateRangeEnd: Date | null,
    _hoveredSecondaryDate: Date | null,
    _anchorDate: Date | null,
    _isDateRange: boolean,
    _viewedMonth: number,
    _viewedYear: number
  ) => {
    const baseClasses = `day ${getBaseClassesForDay(
      day,
      _viewedMonth,
      _viewedYear
    )}`;
    const currentDate = new Date(viewedYear, viewedMonth, day);
    const dateIsInRange = isInRange(
      currentDate,
      selectedDateRangeStart,
      selectedDateRangeEnd,
      hoveredSecondaryDate,
      anchorDate
    );
    const dateIsSelectedStartOrEnd = isSelectedStartOrEnd(
      currentDate,
      selectedDateRangeStart,
      selectedDateRangeEnd,
      hoveredSecondaryDate,
      anchorDate,
      isDateRange
    );
    const dateIsInFuture = isFuture(currentDate);

    const hoverable =
      !dateIsInRange &&
      !dateIsSelectedStartOrEnd &&
      (!disableFuture || !dateIsInFuture);
    const inRange = dateIsInRange;
    const selectedStartEnd = dateIsSelectedStartOrEnd;
    const disabledDay = disableFuture && dateIsInFuture;

    const classes = [
      baseClasses,
      includeClass(hoverable, "hoverable"),
      includeClass(inRange, "in-range"),
      includeClass(selectedStartEnd, "selected-start-end"),
      includeClass(disabledDay, "disabled-day"),
    ];
    return classes.join(" ");
  };
</script>

<style>
  #wrapper {
    --theme-accent-light: hsla(
      var(--theme-accent-h),
      calc(0.5 * var(--theme-accent-s)),
      calc(1.75 * var(--theme-accent-l)),
      var(--theme-accent-a)
    );
    width: 22em;
    display: flex;
    flex-direction: column;
    align-items: center;
    user-select: none;
    /* goddammit safari */
    -webkit-user-select: none;
  }

  .border {
    padding: 1em;
    border: 1px solid #cccccc;
    border-radius: 8px;
  }

  .calendar-container {
    display: grid;
    /* Seven days in a week */
    grid-template-columns: repeat(7, 2.75em);
    /* Max six weeks in a month */
    /* one row for day of the week labels */
    grid-template-rows: repeat(7, 2.75em);
    row-gap: 0.25em;
    column-gap: 0.25em;
  }

  .sunday {
    grid-column-start: 1;
    grid-column-end: span 1;
  }

  .monday {
    grid-column-start: 2;
    grid-column-end: span 1;
  }

  .tuesday {
    grid-column-start: 3;
    grid-column-end: span 1;
  }

  .wednesday {
    grid-column-start: 4;
    grid-column-end: span 1;
  }

  .thursday {
    grid-column-start: 5;
    grid-column-end: span 1;
  }

  .friday {
    grid-column-start: 6;
    grid-column-end: span 1;
  }

  .saturday {
    grid-column-start: 7;
    grid-column-end: span 1;
  }

  .day-labels {
    margin: 0.1em;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5em;
    width: 1.5em;
    padding: 0.5em;
    grid-row-start: 1;
    grid-row-end: span 1;
  }

  .week-1 {
    grid-row-start: 2;
    grid-row-end: span 1;
  }

  .week-2 {
    grid-row-start: 3;
    grid-row-end: span 1;
  }

  .week-3 {
    grid-row-start: 4;
    grid-row-end: span 1;
  }

  .week-4 {
    grid-row-start: 5;
    grid-row-end: span 1;
  }

  .week-5 {
    grid-row-start: 6;
    grid-row-end: span 1;
  }

  .week-6 {
    grid-row-start: 7;
    grid-row-end: span 1;
  }

  .day {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0.5em;
    border-radius: 1.5em;
  }

  .hoverable {
  }

  .hoverable:hover {
    transition-property: background;
    transition-timing-function: cubic-bezier();
    transition-duration: 150ms;
    border: 1px solid hsla(0, 0%, 0%, 0.1);
  }

  .marked-day {
    border: 1px solid var(--theme-accent);
  }

  .legend-day {
    margin: 0.1em;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5em;
    width: 1.5em;
    padding: 0.5em;
    border-radius: 1.5em;
  }

  #selected-day {
    color: white;
    background-color: var(--theme-accent);
  }

  .disabled-day {
    color: #757575;
    cursor: initial;
    opacity: 50%;
  }

  #legend {
    display: flex;
    align-items: center;
    margin-top: 1em;
    justify-content: space-between;
  }

  .legend-item {
    display: flex;
    width: 40%;
    align-items: center;
    justify-content: space-evenly;
  }

  #title-container {
    display: flex;
    margin-bottom: 1em;
  }

  #title {
    flex: 1;
    text-align: center;
    font-weight: bold;
  }

  .month-switcher:hover {
    cursor: pointer;
  }

  .in-range {
    color: white;
    background-color: var(--theme-accent-light);
    border-radius: 0px;
    border-left: 0.125em solid var(--theme-accent-light);
    border-right: 0.125em solid var(--theme-accent-light);
    margin-left: -0.125em;
    margin-right: -0.125em;
  }
  .selected-start-end {
    color: white;
    background-color: var(--theme-accent);
  }
  #selected-start-background {
    background-color: var(--theme-accent-light);
    border-right: 0.125em solid var(--theme-accent-light);
    margin-right: -0.125em;
    border-radius: 1.5em 0 0 1.5em;
    height: 100%;
    width: 100%;
  }
  #selected-end-background {
    background-color: var(--theme-accent-light);
    border-left: 0.125em solid var(--theme-accent-light);
    margin-left: -0.125em;
    border-radius: 0 1.5em 1.5em 0;
    height: 100%;
    width: 100%;
  }
</style>

<div id="wrapper">
  <div class:border={showBorder}>
    <div id="title-container">
      <div class="month-switcher" on:click={modifyMonth(-1)}>
        <ChevronLeft20 />
      </div>
      <div id="title">{calendarTitle}</div>
      <div class="month-switcher" on:click={modifyMonth(1)}>
        <ChevronRight20 />
      </div>
    </div>
    <div class="calendar-container">
      {#each range(0, 7) as day}
        <div class={`day-labels ${dayOfWeekAbbrvs[day]}`}>
          {dayOfWeekAbbrvs[day].slice(0)[0]}
        </div>
      {/each}
      {#each range(1, daysInMonth + 1) as day}
        {#if isSelectedStart(new Date(viewedYear, viewedMonth, day), selectedDateRangeStart, selectedDateRangeEnd, hoveredSecondaryDate, anchorDate, isDateRange) && !selectedStartAndEndAreSame}
          <div
            class={getBaseClassesForDay(day, viewedMonth, viewedYear)}
            id="selected-start-background" />
        {/if}
        {#if isSelectedEnd(new Date(viewedYear, viewedMonth, day), selectedDateRangeStart, selectedDateRangeEnd, hoveredSecondaryDate, anchorDate, isDateRange) && !selectedStartAndEndAreSame}
          <div
            class={getBaseClassesForDay(day, viewedMonth, viewedYear)}
            id="selected-end-background" />
        {/if}
        <div
          class={calculateClassesForDay(day, selectedDateRangeStart, selectedDateRangeEnd, hoveredSecondaryDate, anchorDate, isDateRange, viewedMonth, viewedYear)}
          on:mouseenter={!disableFuture || !isFuture(new Date(viewedYear, viewedMonth, day)) ? setHoverDate(new Date(viewedYear, viewedMonth, day)) : noop}
          on:click={!disableFuture || !isFuture(new Date(viewedYear, viewedMonth, day)) ? selectDate(new Date(viewedYear, viewedMonth, day)) : noop}
          id={getIdForDayDiv(day)}>
          {day}
        </div>
      {/each}
    </div>
  </div>
  {#if showLegend}
    <div id="legend">
      <div class="legend-item">
        <div class="legend-day" id="selected-day" />
        Selected Date
      </div>
      <div class="legend-item">
        <div class="legend-day marked-day" />
        {markedDateLabel}
      </div>
    </div>
  {/if}
</div>
